import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery } from '@apollo/client'
import { toast } from 'sonner'
import { Loader2, Save } from 'lucide-react'
import { z } from 'zod'
import { PageContent } from '@/components/shared/page-content'
import { HeaderSection } from '@/components/shared/header-section'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Form, FormControl, FormItem, FormMessage } from '@/components/ui/form'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import TagsSelect from '@/components/shared/inputComponents/TagsSelect'
import { cn } from '@/lib/utils'
import ModuleBlueprintsCard from './components/ModuleBlueprintsCard'
import {
  GET_PRODUCT_SETTINGS,
  UPDATE_PRODUCT_SETTINGS,
} from './productSettingsQueries'

const productSettingsFormSchema = z.object({
  anonymous_product_tag_ids: z.array(z.number()),
  // Empty input must be rejected, not coerced to 0 (which would silently
  // disable the badge).
  new_badge_days: z.preprocess(
    v => (v === '' || v === null || v === undefined ? undefined : Number(v)),
    z
      .number({
        required_error: 'Enter a number of days (0 disables)',
        invalid_type_error: 'Enter a number of days (0 disables)',
      })
      .int()
      .min(0)
      .max(365)
  ),
})

type ProductSettingsFormValues = z.infer<typeof productSettingsFormSchema>

export default function ProductSettings() {
  const { data, loading, error } = useQuery(GET_PRODUCT_SETTINGS)
  const [updateProductSettings, { loading: saving }] = useMutation(
    UPDATE_PRODUCT_SETTINGS,
    {
      onError: () => toast.error('Failed to save product settings'),
    }
  )

  const form = useForm<ProductSettingsFormValues>({
    resolver: zodResolver(productSettingsFormSchema),
    mode: 'onSubmit',
    defaultValues: { anonymous_product_tag_ids: [], new_badge_days: 0 },
  })

  const shopSetting = data?.findFirstShopSetting
  // Query succeeded but returned nothing (no `ShopSetting` row yet) or failed
  // outright — either way there's no `id` to save against, so the form must
  // not look editable/saveable. See the banner + disabled Card below and the
  // `onSubmit` guard.
  const loadFailed = !loading && !shopSetting

  useEffect(() => {
    if (loading) return

    form.reset({
      anonymous_product_tag_ids: shopSetting?.anonymous_product_tag_ids ?? [],
      new_badge_days: shopSetting?.new_badge_days ?? 0,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data])

  const onSubmit = async (values: ProductSettingsFormValues) => {
    if (!shopSetting) {
      toast.error(
        'Product settings could not be loaded, so there is nothing to save. Reload the page and try again.'
      )
      return
    }

    try {
      // `shopSettingsId` must always be sent — the resolver silently no-ops
      // without it — and `anonymous_product_tag_ids` must always be sent as
      // a bare array (full-replace semantics, `[]` clears it), even when
      // empty. See `productSettingsQueries.ts` doc comment.
      await updateProductSettings({
        variables: {
          shopSettingsId: shopSetting.id,
          anonymous_product_tag_ids: values.anonymous_product_tag_ids,
          new_badge_days: Number(values.new_badge_days),
        },
      })
      toast.success('Product settings saved')
    } catch (saveError) {
      console.error('Save product settings error:', saveError)
    }
  }

  if (loading) {
    return (
      <PageContent>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </PageContent>
    )
  }

  return (
    <PageContent>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex justify-between items-center mb-8">
            <HeaderSection
              title="Product"
              subtitle="Configure which products anonymous B2B portal visitors can see"
            />
            <Button type="submit" disabled={saving || loadFailed}>
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save
            </Button>
          </div>

          {loadFailed && (
            <div className="max-w-[1000px] mb-4 rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error
                ? `Failed to load product settings: ${error.message}`
                : 'Product settings are unavailable right now, so changes here cannot be saved.'}
            </div>
          )}

          <div className="max-w-[1000px] space-y-6">
            <div
              className={cn(loadFailed && 'pointer-events-none opacity-60')}
              aria-disabled={loadFailed}
            >
              <Card className="p-6">
                <InputFieldContainer
                  label="Tags visible to logged-out visitors"
                  description="When set, visitors who are not logged in only see products carrying at least one of these tags; leave empty to show products from the default price list as before. Logged-in customers are always filtered by the tags on their own account and are unaffected by this setting."
                  control={form.control}
                  name="anonymous_product_tag_ids"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <TagsSelect field={field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </Card>

              <Card className="p-6 mt-6">
                <InputFieldContainer
                  label="New badge (days)"
                  description='Products and fabrics created within this many days show a "New" badge in the B2B portal. 0 = disabled.'
                  control={form.control}
                  name="new_badge_days"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          max={365}
                          step={1}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </Card>
            </div>

            {/* Independent of the tags form/shopSetting above -- it queues
                renders via its own mutation, so it stays enabled even when
                `loadFailed` disables the tags card. */}
            <ModuleBlueprintsCard />
          </div>
        </form>
      </Form>
    </PageContent>
  )
}
